import { React, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuBar from '../../Menubar/MenuBar';
import { useMapContext } from '../../../contexts/MapContext';
import BeforeAfterPlanSummaryTable from './CompareResultsTables/BeforeAfterPlanSummaryTable';
import SystemResultsTable from './CompareResultsTables/SystemResultsTable';
import MapDisplay from '../../Map/MapDisplay';
import BeforeAfterHighlightLegend from './CompareResultsTables/BeforeAfterHighlightLegend';
import MapControlPanel from '../../Map/MapControlPanel/MapControlPanel';
import {
    getColorLayer_compareRes,
    getLIDLayer_compareRes,
    getOutfallColor,
    getSelectedFeatureBorder_compareRes,
    getColorLayer,
    getLIDLayer,
    getSelectedFeatureBorder,
} from "../../Map/MapControlPanel/getColorFunctions";
import AvgFloodingComparisonTable from './CompareResultsTables/AvgFloodingComparisonTable';
import LoadingLogoSmall from '../../../utils/LoadingLogoSmall';
import {useLIDContext} from "../../../contexts/LIDContext";


const CompareResults = () => {
    const navigate = useNavigate();
    const { 
        userData,
        runComparison, 
        beforePlan, 
        afterPlan, 
        beforeStorm, 
        afterStorm,
        colorScheme,
        selectedFeature,
        LIDMode,
        LIDContent,
        setGeoJSONData,
    } = useMapContext();
    const { devPlanContent } = useLIDContext();


    const [beforeLID, setBeforeLID] = useState(null);
    const [afterLID, setAfterLID] = useState(null);
    const [dataBefore, setDataBefore] = useState(null);
    const [dataAfter, setDataAfter] = useState(null);

    const [loading, setLoading] = useState(false); 
    const [compareAvgFloodRes, setCompareAvgFloodRes] = useState(new Set());

    const before_dev_plan = userData?.id?`${userData.id}_${beforePlan}` : null;
    const after_dev_plan = userData?.id?`${userData.id}_${afterPlan}` : null;

    const beforeSubcatchment = beforeLID ? Object.keys(beforeLID)[0] : null;
    const afterSubcatchment = afterLID ? Object.keys(afterLID)[0] : null;


    // Go back to the main page when userData is not ready yet
    useEffect(() => {
        if (!userData?.id) {
            navigate('/');
        }
    }, [userData, navigate]) 


    useEffect(() => {
        if (!userData?.id) return;
        const getSystemResults = async () => {
            try {
                const urlBefore = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/system-results/${beforeStorm}/${before_dev_plan}`;
                const urlAfter = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/system-results/${afterStorm}/${after_dev_plan}`;

                const [responseBefore, responseAfter] = await Promise.all([
                    fetch(urlBefore), fetch(urlAfter)
                ]);

                if (!responseBefore.ok) throw new Error(`Failed to get system-results ${urlBefore}`);
                if (!responseAfter.ok) throw new Error(`Failed to get system-results ${urlAfter}`);

                const [data_before, data_after] = await Promise.all([
                    responseBefore.json(), responseAfter.json()
                ]);

                setDataBefore(data_before);
                setDataAfter(data_after);
            } catch (error) {
                console.error(`System results fetch failed: ${error.message}`);
            }
        };

        getSystemResults();
    }, [runComparison]);
    // }, [runComparison, beforeStorm, afterStorm, before_dev_plan, after_dev_plan]);


    useEffect(() => {
        const readLID = async () => {
            try {
                const urlBefore = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read-lid/GLWA_${beforeStorm}_${before_dev_plan}`;
                const urlAfter = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read-lid/GLWA_${afterStorm}_${after_dev_plan}`;

                const [responseBefore, responseAfter] = await Promise.all([
                    fetch(urlBefore), fetch(urlAfter)
                ]);

                if (!responseBefore.ok) throw new Error(`Failed to read LID ${urlBefore}`);
                if (!responseAfter.ok) throw new Error(`Failed to read LID ${urlAfter}`);

                const [data_before, data_after] = await Promise.all([
                    responseBefore.json(), responseAfter.json()
                ]);

                setBeforeLID(data_before);
                setAfterLID(data_after);
            } catch (error) {
                console.error(`Read LID fetch failed: ${error.message}`);
            }
        };

        readLID();
    }, [runComparison]);
    // }, [runComparison, beforeStorm, afterStorm, before_dev_plan, after_dev_plan]);


    useEffect(() => {
        setLoading(!(beforeLID && afterLID && dataBefore && dataAfter));
    }, [beforeLID, afterLID, dataBefore, dataAfter]);



    // useEffect(() => {
    //     const checkFileExists = async () => {
    //         const filename = `${beforeStorm}_${before_dev_plan}__VS__${afterStorm}_${after_dev_plan}_COMPARE_AVG_FLOODING.geojson`;
    //         const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/checkFileExists/tycoon-bucket162046-main/public__Detroit__Compare_dev_plans/${filename}`;
    //         const response = await fetch(url);
    //         const data = await response.json();
    //         return data.exists;
    //     }

    //     const uploadComparison = async () => {
    //         try {
    //             const uploadResponse = await fetch(
    //                 `https://2471d2z813.execute-api.us-east-2.amazonaws.com/upload_plans_avgFlooding_diff/Aug16/${before_dev_plan}/Aug16/${after_dev_plan}`,
    //                 { method: "POST" }
    //             );

    //             if (!uploadResponse.ok) {
    //                 console.error("Upload failed:", await uploadResponse.text());
    //                 throw new Error("Failed to upload comparison result.");
    //             }

    //         } catch (error) {
    //         console.error("Comparison pipeline error:", error.message);
    //         }
    //     };

    //     const fetchComparison = async () => {
    //         const resultResponse = await fetch(
    //             `https://2471d2z813.execute-api.us-east-2.amazonaws.com/get_plans_avgFlooding_diff/Aug16/${before_dev_plan}/Aug16/${after_dev_plan}`
    //         );

    //         if (!resultResponse.ok) {
    //             console.error("Fetch result failed:", await resultResponse.text());
    //             throw new Error("Failed to fetch comparison result.");
    //         }
    //             const { url } = await resultResponse.json();
    //             const geojsonResponse = await fetch(url);
    //             if (!geojsonResponse.ok) {
    //                 console.error("GeoJSON fetch failed:", await geojsonResponse.text());
    //                 throw new Error("Failed to fetch GeoJSON data.");
    //             }
    //             const geojsonData = await geojsonResponse.json();
    //             setCompareAvgFloodRes(prev => [...prev, geojsonData]);
    //             console.log("GeoJSON data:", geojsonData);
    //     }

    //     const fileExists = checkFileExists();
    //     console.log("fileExists", fileExists);
    //     if (!fileExists) {
    //         uploadComparison();
    //     }
    //     fetchComparison();

    // }, [beforeStorm, before_dev_plan, afterStorm, after_dev_plan]);


    useEffect(() => {
        const runComparisonLogic = async () => {
            try {
                const filename = `${beforeStorm}_${before_dev_plan}__VS__${afterStorm}_${after_dev_plan}_COMPARE_AVG_FLOODING.geojson`;
                const checkUrl = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/checkFileExists/tycoon-bucket162046-main/public__Detroit__Compare_dev_plans/${filename}`;
                
                const response = await fetch(checkUrl);
                const data = await response.json();
                const fileExists = data.exists;

                console.log("fileExists", fileExists);

                if (!fileExists) {
                    const uploadResponse = await fetch(
                        `https://2471d2z813.execute-api.us-east-2.amazonaws.com/upload_plans_avgFlooding_diff/Aug16/${before_dev_plan}/Aug16/${after_dev_plan}`,
                        { method: "POST" }
                    );

                    if (!uploadResponse.ok) {
                        console.error("Upload failed:", await uploadResponse.text());
                        throw new Error("Failed to upload comparison result.");
                    }
                }

                console.log("file name: ", `Aug16/${before_dev_plan}/Aug16/${after_dev_plan}`);

                const resultResponse = await fetch(
                    `https://2471d2z813.execute-api.us-east-2.amazonaws.com/get_plans_avgFlooding_diff/Aug16/${before_dev_plan}/Aug16/${after_dev_plan}`
                );

                if (!resultResponse.ok) {
                    console.error("Fetch result failed:", await resultResponse.text());
                    throw new Error("Failed to fetch comparison result.");
                }

                const { url } = await resultResponse.json();
                const geojsonResponse = await fetch(url);

                if (!geojsonResponse.ok) {
                    console.error("GeoJSON fetch failed:", await geojsonResponse.text());
                    throw new Error("Failed to fetch GeoJSON data.");
                }

                const res = await geojsonResponse.json();
                setCompareAvgFloodRes(prev => {
                    const newSet = new Set(prev);
                    newSet.add(res);
                    return newSet;
                });
                console.log("GeoJSON data:", res);
            } catch (error) {
                console.error("Comparison pipeline error:", error.message);
            }
        };

        runComparisonLogic();
    }, [beforeStorm, before_dev_plan, afterStorm, after_dev_plan]);




    useEffect(() => {
        setGeoJSONData((prevData) => ({
            ...prevData,
            subcatchments: Array.from(compareAvgFloodRes)[0]
        }))
    }, [compareAvgFloodRes, setGeoJSONData]);



    // const getUniqueAvgFloodingValues = (geojsonSet) => {
    //     const geojson = Array.from(geojsonSet)[0];
    //     if (!geojson || !geojson.features) return [];

    //     const uniqueSet = new Set(
    //         geojson.features.map(feature => feature.properties?.avgFlooding)
    //     );

    //     return Array.from(uniqueSet);
    // };

    // useEffect(() => {
    //     const uniqueValues = getUniqueAvgFloodingValues(compareAvgFloodRes);
    //     console.log("Unique avgFlooding values:", uniqueValues);
    // }, [compareAvgFloodRes]);






    const calculateDifference = () => {
        if (dataBefore && dataAfter) {
            return {
                flooding: dataAfter.flooding - dataBefore.flooding,
                infiltration: dataAfter.infiltration - dataBefore.infiltration,
                outflow: dataAfter.outflow - dataBefore.outflow,
                runoff: dataAfter.runoff - dataBefore.runoff,
            };
        }
        return null;
    };

    const difference = calculateDifference();



    return (
        <div style={{ backgroundColor: '#000', color: '#fff', minHeight: '100vh', padding: 0, overflowY: 'auto', maxHeight: '100vh' }}>
            <MenuBar />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '80vh', width: '100%' }}>
                <h1 style={{ color: '#2196F3', marginTop: 32, marginBottom: 32, textAlign: 'center' }}>
                    Compare Results of Two Development Plans
                </h1>

                {loading ? (
                    <LoadingLogoSmall message="Loading comparison results..." />
                ) : ((!userData?.id) || (!beforeLID) || (!afterLID) || (!dataAfter) || (!dataBefore)) ? (
                    <div style={{ textAlign: 'center', marginTop: 24, marginBottom: 24 }}>
                        <WarningAmberIcon style={{ color: '#D32F2F', fontSize: '40px' }} />
                        <p style={{ color: '#D32F2F', fontSize: '18px', fontWeight: 'bold', marginTop: '5px' }}>
                            Please select two development plans before running the comparison.
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={{ width: '90%', maxWidth: 1200, marginBottom: 32 }}>
                            <BeforeAfterHighlightLegend />
                        </div>

                        <div style={{ width: '90%', maxWidth: 1200, height: 500, marginBottom: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <MapDisplay>
                                <MapControlPanel
                                    getColorLayer={() => getColorLayer_compareRes(colorScheme, selectedFeature.ID, LIDMode, LIDContent, beforeSubcatchment, afterSubcatchment)}
                                    getLIDLayer={() => getLIDLayer_compareRes(colorScheme, selectedFeature.ID, LIDMode, LIDContent, beforeSubcatchment, afterSubcatchment)}
                                    getSelectedFeatureBorder={() => getSelectedFeatureBorder_compareRes(colorScheme, selectedFeature.ID, LIDMode, LIDContent, beforeSubcatchment, afterSubcatchment)}
                                    getOutfallColor={getOutfallColor}
                                />
                            </MapDisplay>
                        </div>

                        <div style={{ width: '90%', maxWidth: 1200, marginBottom: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <BeforeAfterPlanSummaryTable {...{ beforeLID, afterLID, tableHeaderStyle, tableCellStyle }} />
                        </div>

                        <div style={{ width: '90%', maxWidth: 1200, marginBottom: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {dataBefore && dataAfter ? (
                                <SystemResultsTable {...{ dataBefore, dataAfter, difference, tableHeaderStyle, tableCellStyle }} />
                            ) : (
                                <p>Loading data...</p>
                            )}
                        </div>

                        {/* Average Flooding Map Comparison */}
                        {compareAvgFloodRes && (
                            <>
                                <div style={{ width: '90%', maxWidth: 1200, height: 500, marginBottom: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <h1 style={{ color: '#2196F3', marginTop: 32, marginBottom: 32, textAlign: 'center' }}>
                                        Compare Average Flooding
                                    </h1>
                                    <MapDisplay>
                                        <MapControlPanel
                                            getColorLayer={() => getColorLayer("compare_flooding", selectedFeature.ID, LIDMode, LIDContent, Array.from(compareAvgFloodRes)[0])}
                                            getLIDLayer={() => getLIDLayer("compare_flooding", selectedFeature.ID, LIDMode, LIDContent, devPlanContent)}
                                            getSelectedFeatureBorder={() => getSelectedFeatureBorder("compare_flooding", selectedFeature.ID, LIDMode, LIDContent, devPlanContent)}
                                            getOutfallColor={getOutfallColor}
                                        />
                                    </MapDisplay>
                                </div>
                                <div style={{ width: '90%', maxWidth: 1200, display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 auto', marginBottom: 32 }}>
                                    <AvgFloodingComparisonTable
                                        selectedFeature={selectedFeature}
                                        compareAvgFloodRes={compareAvgFloodRes}
                                        tableHeaderStyle={tableHeaderStyle}
                                        tableCellStyle={tableCellStyle}
                                    />
                                </div>
                            </>
                        )}

                        <button
                            onClick={() => navigate('/')}
                            style={{
                                backgroundColor: '#2196f3',
                                color: '#ffffff',
                                padding: '12px 24px',
                                fontSize: '18px',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                marginTop: '24px',
                                alignSelf: 'center',
                                marginBottom: '32px',
                                minWidth: 180
                            }}
                        >
                            Back to Map
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};




const tableHeaderStyle = {
    border: '2px solid #2196F3',
    padding: '12px',
    textAlign: 'center',
    backgroundColor: '#1E1E1E',
    color: '#FFFFFF',
    fontWeight: 'bold',
};

const tableCellStyle = {
    border: '1px solid #2196F3',
    padding: '10px',
    textAlign: 'center',
    backgroundColor: '#121212',
    color: '#FFFFFF'
};

export default CompareResults;



